import { ChartPie } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, Sector, Tooltip } from "recharts";
import styled from "styled-components";
import { PALETTE, tooltipStyle } from "./constants";
import type { IStatBarChart, IStatItem } from "./types";
import { SummaryCard } from "./SummaryCard";
import { formatSummaryNumber, summaryBreakdown } from "./summaryData";
import { rankingSeriesColor } from "./rankingTheme";
import { DistributionModeSelector } from "./ChartModeSelector";
import { useDistributionMode } from "./useDistributionMode";
import { useDonutAnimation } from "./useDonutAnimation";

const DonutLayout = styled.div`
	display: grid; grid-template-columns: 136px minmax(0, 1fr); align-items: center; gap: 12px; margin: auto 0;
	.donut { position: relative; width: 136px; height: 136px; justify-self: center; }
	.donut-center { position: absolute; z-index: 1; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; pointer-events: none; }
	.donut-center strong { max-width: 86px; font-size: 15px; font-weight: 600; text-align: center; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
	.donut-center .caption { font-size: 11px; }
	.breakdown { column-gap: 8px; }
	.breakdown > div { border-radius: 4px; transition: background-color 180ms ease; }
	.breakdown > div[data-active="true"] { background: #f6f8fb; box-shadow: 4px 0 #f6f8fb, -4px 0 #f6f8fb; }
	.breakdown > div:focus-visible { outline: 2px solid #167ca6; outline-offset: 2px; }
	@media (prefers-reduced-motion: reduce) { .breakdown > div { transition: none; } }
	.count { font-size: 13px; }
	@container (max-width: 360px) { grid-template-columns: minmax(0, 1fr); gap: 8px; }
`;

const StatusLegend = styled.dl`
	/* Keep status, count and share on one compact row, including narrow cards. */
	&&.breakdown { grid-template-columns: minmax(0, 1fr) max-content max-content; column-gap: 8px; }
	&&.breakdown > div { padding: 4px 0; min-height: 26px; align-items: center; line-height: 1.35; }
	&&.breakdown dt { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; }
	&&.breakdown .dot { display: inline-block; width: 6px; height: 6px; margin-right: 6px; vertical-align: 1px; }
	&&.breakdown .count { grid-column: 2; font-size: 12px; white-space: nowrap; }
	&&.breakdown .share { grid-column: 3; font-size: 11px; white-space: nowrap; }
`;

const STATUS_COLORS = new Map([
	["failed", "#d96a77"],
	["completed", "#3f9f69"],
	["expired", "#98a2b3"],
]);
const FALLBACK_COLORS = PALETTE.filter(color => color !== "#10b981" && color !== "#ef4444");

// Keep every API row; maxItems never limited this card. Both views share the sorted distribution.
export const PieChartCard = ({ item, preferenceKey }: { item: IStatItem; preferenceKey: string }) => {
	const { t, i18n } = useTranslation();
	const [mode, changeMode] = useDistributionMode(preferenceKey);
	const [highlight, setHighlight] = useState<{ data: unknown; key: string; index: number } | null>(null);
	const activeIndex = mode === "donut" && highlight?.data === item.data && highlight?.key === preferenceKey ? highlight.index : null;
	const deactivate = () => setHighlight(null);
	const format = (value: number | null) => formatSummaryNumber(value, i18n.language);
	const formatShare = (value: number | null) => value === null ? "—" : value > 0 && value < 0.1
		? `<${(0.1).toLocaleString(i18n.language)}%`
		: `${value.toLocaleString(i18n.language, { maximumFractionDigits: 1 })}%`;
	const { rows, total } = useMemo(() => {
		const source: IStatBarChart[] = Array.isArray(item.data) ? item.data : [];
		const { values, total, percentages } = summaryBreakdown(source.map(row => row.value));
		let dynamicIndex = 0;
		const rows = source.map((row, index) => {
			const statusColor = STATUS_COLORS.get(row.label.toLowerCase());
			const fallback = statusColor ?? FALLBACK_COLORS[dynamicIndex++ % FALLBACK_COLORS.length];
			return { sourceIndex: index, label: row.label, value: values[index], share: percentages[index], color: rankingSeriesColor(item.colors?.[index] || fallback) };
		});
		// Resolve colors before sorting. Preserve the server order for ties; missing counts go last.
		rows.sort((a, b) => (b.value ?? -1) - (a.value ?? -1) || a.sourceIndex - b.sourceIndex);
		return { rows, total };
	}, [item.data, item.colors]);
	const activate = (index: number) => setHighlight(total !== null && total > 0 && (rows[index]?.value ?? 0) > 0 ? { data: item.data, key: preferenceKey, index } : null);
	const animation = useDonutAnimation(activeIndex, rows.length, item.data, preferenceKey);
	const breakdown = <StatusLegend className="breakdown">
		{rows.map((row, index) => <div key={row.sourceIndex}
			data-active={activeIndex === index || undefined}
			tabIndex={mode === "donut" && total !== null && total > 0 && (row.value ?? 0) > 0 ? 0 : undefined}
			onMouseEnter={mode === "donut" ? () => activate(index) : undefined} onMouseLeave={deactivate}
			onFocus={mode === "donut" ? () => activate(index) : undefined} onBlur={deactivate}>
			<dt title={row.label}><span className="dot" aria-hidden="true" style={{ background: row.color }} />{row.label}</dt>
			<dd className="count">{format(row.value)}</dd>
			<dd className="share" title={row.share === null ? undefined : `${row.share.toLocaleString(i18n.language, { maximumFractionDigits: 6 })}%`}>{formatShare(row.share)}</dd>
		</div>)}
	</StatusLegend>;
	return <SummaryCard title={item.title} icon={<ChartPie size={20} aria-hidden="true" />} prefix={item.prefix}
		actions={<DistributionModeSelector value={mode} onChange={next => { deactivate(); changeMode(next); }} />}>
		{rows.length > 0 ? mode === "donut" ? <DonutLayout>
			<div className="donut">
				{total !== null && total > 0 ? <PieChart width={136} height={136}>
					<Pie data={rows} dataKey="value" nameKey="label" innerRadius={46} outerRadius={64} startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}
						onMouseEnter={(_row, index) => activate(index)} onMouseLeave={deactivate}
						shape={props => <g>
							{/* Keep pointer detection independent of the animated geometry. */}
							<Sector {...props} fill="transparent" stroke="none" />
							<Sector {...props} pointerEvents="none" aria-hidden="true" className="donut-sector"
								outerRadius={props.outerRadius + 4 * (animation.growth[props.index] ?? 0)}
								opacity={1 - 0.35 * Math.max(0, animation.presence - (animation.growth[props.index] ?? 0))} />
						</g>}>
						{rows.map(row => <Cell key={row.sourceIndex} fill={row.color} />)}
					</Pie>
					<Tooltip wrapperStyle={{ zIndex: 10 }} contentStyle={tooltipStyle} formatter={(value, name) => [`${format(Number(value))} (${formatShare(Number(value) / total * 100)})`, String(name)]} />
				</PieChart> : <svg width="136" height="136" viewBox="0 0 136 136" aria-hidden="true"><circle cx="68" cy="68" r="55" fill="none" stroke="#eef1f5" strokeWidth="18" /></svg>}
				<div className="donut-center"><strong>{format(total)}</strong><span className="caption">{t("stats.labels.total")}</span></div>
			</div>
			{breakdown}
		</DonutLayout> : <>
			<div className="caption" style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
				<span>{t("stats.labels.total")}</span><strong style={{ color: "#344054", fontSize: 16, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{format(total)}</strong>
			</div>
			<div className="track" role="img" aria-label={rows.map(row => `${row.label}: ${format(row.value)} (${formatShare(row.share)})`).join("; ")}>
				{rows.map(row => <span key={row.sourceIndex} className="segment" title={`${row.label}: ${format(row.value)} (${formatShare(row.share)})`} style={{ width: `${row.share ?? 0}%`, background: row.color }} />)}
			</div>
			{breakdown}
		</> : <p className="caption" role="status">{t("common.noData")}</p>}
	</SummaryCard>;
};
